import { ProductNewModel } from "../../models/products/product-new-model";
import React, { useEffect, useRef, useState } from "react";
import ProductService from "../../services/products/products.service";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Helpers from "../../utils/helpers";
import { useTranslation } from "react-i18next";
import { InvoiceProduct } from "../../models/invoice/invoice-product-insert-model";
import { Toast, ToastMessage } from "primereact/toast";
import ProductInvoice from "../../services/invoices/gen_inv.service";
import AppLoading from '../app-loading/app-loading';
import { InputText } from "primereact/inputtext";
import { OrderDetail } from "../../models/orders/order-list-model";

export const NewAddProduct = ({ orderedItems, updateItems }: any) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState<ProductNewModel[]>([]);
  const [quantities, setQuantities] = useState<{ [key: number]: number }>({});
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState<string>("");

  const showMessage = (
    label: string,
    message: string,
    severity: ToastMessage["severity"]
  ) => {
    toast.current?.show({
      severity: severity,
      summary: label,
      detail: message,
      life: 3000,
    });
  };

  // ✅ YOUR CHANGE: uses productImageList?.mediaUrl with onError fallback
  const imageBodyTemplate = (item: any) => {
    const imageUrl = (item.productImageList?.mediaUrl || process.env.PUBLIC_URL + "/assests/images/Desiri-Groundnut-Oils.png");
    return (
      <img
        src={imageUrl}
        alt={item.name}
        onError={(e) => {
          (e.target as HTMLImageElement).src = "/assests/images/Desiri-Groundnut-Oils.png";
        }}
        style={{ objectFit: 'contain', borderRadius: '4px', width: '50px', height: '50px', display: 'block' }}
      />
    );
  };

  const addProductBtn = (product: any) => {
    return (
      <Button label="Add" onClick={() => handleAdd(product.id)} />
    );
  };

  const unitPriceBodyTemplate = (product: any) => {
    return Helpers.formatAmountInINR(product.unitPrice);
  };

  const quantityTemplate = (product: any) => {
    return (
      <div className="flex product-item product-wrapper">
        <div className="product-list-quantity">
          <InputNumber
            inputId={String(product.id)}
            style={{ width: 120 }}
            value={quantities[product.id] || product.minOrderQty}
            showButtons
            buttonLayout="horizontal"
            step={product.orderMultiples}
            decrementButtonClassName="p-button-danger"
            className="quantity-border"
            incrementButtonClassName="p-button-success"
            incrementButtonIcon="pi pi-plus"
            decrementButtonIcon="pi pi-minus"
            min={product.minOrderQty}
            onChange={(e) => setQuantities(prev => ({
              ...prev,
              [product.id]: e.value as number
            }))}
          />
        </div>
      </div>
    );
  };

  useEffect(() => {
    ProductService.getAllProducts()
      .then((response) => {
        var filteredProducts = response.filter((p: any) => p.forCustomer === true);
        setProducts(filteredProducts);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
        setLoading(false);
      });
  }, []);

  const handleAdd = async (id: number) => {

    const productItem = products.find(p => p.id === id);
    if (!productItem) return;

    // ✅ YOUR CHANGE: smart existingItemIndex logic
    const qty = quantities[id] || productItem.minOrderQty;

    const existingItemIndex = (orderedItems.orderDetails || []).findIndex(
      (x: OrderDetail) => x.productID === id
    );

    if (existingItemIndex > -1) {
      const existingItem = orderedItems.orderDetails[existingItemIndex];

      // If already in the current invoice with pending qty > 0 → block duplicate
      if (existingItem.pendingQuantity > 0) {
        showMessage("Add Product", "Product already added", "warn");
        return;
      }

      // If pendingQuantity === 0 (was cancelled/invoiced before) → re-add it
      // (merges DEV's intent of preventing true duplicates + YOUR feature of re-adding)
      const taxableAmount = qty * productItem.unitPrice;
      const taxAmount = taxableAmount * (productItem.tax / 100);
      const gross = taxableAmount + taxAmount;

      const newItem = {
        ...existingItem,
        quantity: qty,
        pendingQuantity: qty,
        taxableAmount,
        taxAmount,
        grossAmount: gross
      };

      const newOrderDetails = [...orderedItems.orderDetails];
      newOrderDetails[existingItemIndex] = newItem;

      updateItems({
        ...orderedItems,
        orderDetails: newOrderDetails
      });

      showMessage("Add Product", "Product added to invoice", "success");

      setQuantities(prev => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });

      return;
    }

    const taxableAmount = qty * productItem.unitPrice;
    const taxAmount = taxableAmount * (productItem.tax / 100);
    const gross = taxableAmount + taxAmount;

    // NEW UI-ONLY ITEM (orderDetailID = 0)
    const newItem: OrderDetail = {
      orderDetailID: 0,
      orderId: orderedItems.orderID,
      productID: productItem.id,
      productName: productItem.name,
      categoryName: productItem.categoryName,
      subCategoryName: productItem.subCategoryName,
      hsN_Code: "",
      orderStatusName: orderedItems.orderStatusName,
      measurementTypeName: "",
      unitPrice: productItem.unitPrice,
      minOrderQty: productItem.minOrderQty,
      orderMultiples: productItem.orderMultiples,
      quantity: qty,
      pendingQuantity: qty,
      taxableAmount,
      gst: productItem.tax,
      taxAmount,
      grossAmount: gross,
      // ✅ DEV CHANGE: correct field name from ProductNewModel
      url: productItem.productImageList?.mediaUrl || process.env.PUBLIC_URL + "/assests/images/Desiri-Groundnut-Oils.png",
      trackingID: "1",
      discountAmount: orderedItems.discountAmount
    };

    // UPDATE UI-STATE
    updateItems({
      ...orderedItems,
      orderDetails: [...(orderedItems.orderDetails || []), newItem]
    });

    showMessage("Add Product", "Product added to invoice", "success");

    // Clear quantity input
    setQuantities(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const filteredData = products.filter((item) =>
    item.name?.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="col-12">
      <Toast ref={toast} position="top-right" />
      <div className="col-12">
        <div className="flex align-items-center flex-wrap gap-2">
          <div className="font-bold text-white border-round p-input-icon-left" style={{ float: "right" }}>
            <i className="fa-solid fa-search"></i>
            <InputText
              id="search"
              name="search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={t("common_search_placeholder")}
            />
          </div>
        </div>
      </div>
      <DataTable
        value={filteredData}
        tableStyle={{ minWidth: '50rem' }}
        globalFilterFields={['name']}
      >
        <Column header="Image" style={{ width: "10%", marginLeft: "8rem" }} body={imageBodyTemplate}></Column>
        <Column
          field="name"
          style={{ width: "50%" }}
          header={t("invoices_product_name")}
        ></Column>
        <Column
          field="unitPrice"
          header={t("invoices_product_price")}
          body={unitPriceBodyTemplate}
        ></Column>
        <Column
          field="quantity"
          header={t("invoices_product_quantity")}
          body={quantityTemplate}
        ></Column>
        <Column field="id" header="Action" body={addProductBtn}></Column>
      </DataTable>
      {loading && <AppLoading />}
    </div>
  );
  
};